import { supabase } from '../config/supabaseClient';

// =====================================================
// TYPES & INTERFACES
// =====================================================

export interface Notification {
  id: string;
  user_id: string;
  type: 'task_assigned' | 'comment_added' | 'task_due_soon' | 'status_changed' | 'mention' | 'proactive_digest' | 'trial_onboarding';
  title: string;
  message: string | null;
  link: string | null;
  entity_type: 'task' | 'project' | 'comment' | 'digest' | null;
  entity_id: string | null;
  read: boolean;
  created_at: string;
  metadata?: any;
}

// =====================================================
// SERVICE IMPLEMENTATION
// =====================================================

export const notificationsService = {
  /**
   * Get all notifications for current user
   */
  async list(unreadOnly: boolean = false): Promise<{ data: Notification[] | null; error: any }> {
    try {
      let query = supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (unreadOnly) {
        query = query.eq('read', false);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching notifications:', error);
        return { data: null, error: error.message };
      }

      return { data, error: null };
    } catch (err: any) {
      console.error('Error in notificationsService.list:', err);
      return { data: null, error: err.message || 'Failed to fetch notifications' };
    }
  },

  /**
   * Get unread count
   */
  async getUnreadCount(): Promise<{ count: number; error: any }> {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('read', false);

      if (error) {
        console.error('Error getting unread count:', error);
        return { count: 0, error: error.message };
      }

      return { count: count || 0, error: null };
    } catch (err: any) {
      console.error('Error in notificationsService.getUnreadCount:', err);
      return { count: 0, error: err.message || 'Failed to get unread count' };
    }
  },

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<{ error: any }> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('Error marking notification as read:', error);
        return { error: error.message };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Error in notificationsService.markAsRead:', err);
      return { error: err.message || 'Failed to mark as read' };
    }
  },

  async markManyAsRead(notificationIds: string[]): Promise<{ error: any }> {
    try {
      const ids = Array.from(
        new Set(
          (notificationIds || [])
            .map((id) => String(id || '').trim())
            .filter(Boolean)
        )
      );

      if (ids.length === 0) return { error: null };

      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .in('id', ids);

      if (error) {
        console.error('Error marking notifications as read:', error);
        return { error: error.message };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Error in notificationsService.markManyAsRead:', err);
      return { error: err.message || 'Failed to mark selected as read' };
    }
  },

  async deleteMany(notificationIds: string[]): Promise<{ error: any }> {
    try {
      const ids = Array.from(
        new Set(
          (notificationIds || [])
            .map((id) => String(id || '').trim())
            .filter(Boolean)
        )
      );

      if (ids.length === 0) return { error: null };

      const { error } = await supabase
        .from('notifications')
        .delete()
        .in('id', ids);

      if (error) {
        console.error('Error deleting notifications:', error);
        return { error: error.message };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Error in notificationsService.deleteMany:', err);
      return { error: err.message || 'Failed to delete selected notifications' };
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ error: any }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { error: 'User not authenticated' };
      }

      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) {
        console.error('Error marking all as read:', error);
        return { error: error.message };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Error in notificationsService.markAllAsRead:', err);
      return { error: err.message || 'Failed to mark all as read' };
    }
  },

  /**
   * Delete a notification
   */
  async delete(notificationId: string): Promise<{ error: any }> {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        console.error('Error deleting notification:', error);
        return { error: error.message };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Error in notificationsService.delete:', err);
      return { error: err.message || 'Failed to delete notification' };
    }
  },

  /**
   * Subscribe to real-time notifications
   */
  subscribeToNotifications(userId: string, callback: (notification: Notification) => void) {
    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          callback(payload.new as Notification);
        }
      )
      .subscribe();

    return channel;
  },

  /**
   * Unsubscribe from notifications
   */
  async unsubscribe(channel: any) {
    if (channel) {
      await supabase.removeChannel(channel);
    }
  },

  /**
   * Get notification icon based on type
   */
  getNotificationIcon(type: Notification['type']): string {
    switch (type) {
      case 'task_assigned': return '👤';
      case 'comment_added': return '💬';
      case 'task_due_soon': return '⏰';
      case 'status_changed': return '🔄';
      case 'mention': return '@';
      case 'proactive_digest': return '📊';
      case 'trial_onboarding': return '✅';
      default: return '🔔';
    }
  },
};
